import classNames from 'classnames';
import { Collapse, UnmountClosed } from 'react-collapse';
import React, { memo, useEffect, useMemo, useState } from 'react';

import { CSS_ANIMATION_DURATION } from 'src/config';
import { SafeAnyType } from 'src/utils/safeAny';
import { ChildrenPropType } from 'src/utils/types';

type Props = {
  show: boolean;
  children: ChildrenPropType;

  noUnmount?: boolean;
  onUnmount?: () => void;
  fullWidth?: boolean;
  className?: string;
  // do not pass className and margin/padding classes in first child elements because it breaks an animation
};

/**
 * animate an additional fade in/out content
 *
 * to show properly without any animation error pass the show param correctly
 *  (especially at the first/initial render)
 */
// almost the same as UiFadeInAnimation
export const UiAdditionalBlockAnimator: React.FC<Props> = memo(
  ({ show, children, onUnmount, noUnmount, fullWidth, className }: Props) => {
    const [isShow, setIsShow] = useState<boolean>(show);
    const [open, setOpen] = useState<boolean>(show);

    /** checking ref to not animate on first render if show=true */
    useEffect(() => {
      if (show && !isShow) {
        setIsShow(true);

        setTimeout(() => {
          setOpen(true);
        }, CSS_ANIMATION_DURATION);
      }
      if (!show && isShow) {
        setIsShow(false);
        setOpen(false);

        if (onUnmount) {
          onUnmount();
        }
      }
    }, [isShow, show, onUnmount]);

    const theme = useMemo(
      () => ({
        collapse: classNames('collapsible-block r-collapsible-block', className, {
          fullWidth,
          open,
        }),
        content: '',
      }),
      [className, fullWidth, open]
    );

    const Component = noUnmount ? Collapse : UnmountClosed;

    return (
      <Component theme={theme} isOpened={isShow} initialStyle={{ height: '0px', overflow: 'hidden' }}>
        {children as SafeAnyType}
      </Component>
    );
  }
);
